import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { MobileInput } from "components/DesignSystem/MobileInput/MobileInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import { Switch } from "components/DesignSystem/Switch/Switch";
import Input from "components/DesignSystem/Input/Input";
import { EmailUpdate } from "components/userProfile/EmailUpdate";
import { MobileUpdate } from "components/userProfile/MobileUpdate";
import { timezones } from "data/timezones";
import { FormikValues } from "formik";
import { manageProfileValidation } from "formValidations/manageProfileValidation";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { ModalClose, useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import {
  useAdminUpdateTeamMemberContactMutation,
  useUpdateTeamMemberMutation,
} from "store/apis/teamSetting";
import { FormikForm } from "components/FormikForm/FormikForm";
const timezoneOptions = timezones
  .map(({ value, text, utc }) => ({
    value: JSON.stringify(utc),
    label: `${value} - ${text}`,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

type ManageProfileProps = {
  show: boolean;
  closeModal: () => void;
  user: {
    email: string;
    name: string;
    first_name: string;
    last_name: string;
    mobile: string;
    linkedIn_url: string;
    current_timezone: string;
    secondary_mobile: string;
    role_name: string;
    group_role_id: string;
    profile_id: string;
    available_secondary_whatsapp: boolean;
    available_primary_whatsapp: boolean;
    calendar_url: string;
    whatsapp_consent: boolean;
  };
};

type ConfirmWhatsappConsentModalProps = {
  isOpen: boolean;
  close: ModalClose;
  onClose: () => void;
};

export const ConfirmWhatsappConsentModal = ({
  isOpen,
  close,
  onClose,
}: ConfirmWhatsappConsentModalProps) => {
  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="small">
        <StatusModal
          type="warning"
          title="Confirmed with the customer?"
          showCloseButton={false}
        >
          <div className="t-text-body-sm t-text-text-30">
            Ensure the customer is aware and has approved this action before you
            continue.
          </div>
        </StatusModal>
        <Modal.FooterButtonGroup>
          <Modal.RawClose asChild>
            <Button type="button" customType="secondary">
              Go back
            </Button>
          </Modal.RawClose>
          <Button type="button" customType="primary" onClick={onClose}>
            Yes, proceed
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};

export const ManageProfile = ({
  show,
  closeModal,
  user,
}: ManageProfileProps) => {
  const emailUpdateModal = useModal();
  const mobileUpdateModal = useModal();
  const { alertToast, successToast } = useToast();
  const {
    email,
    name,
    first_name,
    last_name,
    mobile,
    linkedIn_url,
    current_timezone,
    secondary_mobile,
    group_role_id,
    profile_id,
    available_primary_whatsapp,
    available_secondary_whatsapp,
    calendar_url,
    whatsapp_consent,
  } = user;
  const { roles, uuid: groupId } = useCurrentGroupContext();

  const { isInkleSuperAdmin } = useRoleBasedView();
  const [updateTeamMember, { isLoading: teamMemberUpdating }] =
    useUpdateTeamMemberMutation();
  const [adminUpdateTeamMemberContact, { isLoading: contactUpdating }] =
    useAdminUpdateTeamMemberContactMutation();
  const initialFirstName = first_name || name || "";
  const initialLastName = last_name || "";
  const confirmWhatsappConsent = useModal();

  const roleUpdateNotAllowed =
    group_role_id === process.env.PUBLIC_ACCOUNTANT_ROLE_ID;

  const roleOptions = roleUpdateNotAllowed
    ? roles
    : roles.filter(
        ({ uuid }) => uuid !== process.env.PUBLIC_ACCOUNTANT_ROLE_ID
      );

  const roleComboboxOptions = roleOptions.map(({ uuid, name }) => ({
    value: uuid,
    label: name,
  }));

  const customerInitialValues = {
    first_name: initialFirstName,
    last_name: initialLastName,
    secondary_mobile: secondary_mobile ? `${secondary_mobile}` : "",
    current_timezone,
    linkedin_url: linkedIn_url,
    mobile,
    email,
    role: group_role_id,
    available_primary_whatsapp,
    available_secondary_whatsapp,
    calendar_url,
    whatsapp_consent,
  };

  const handleSubmit = async (values: FormikValues) => {
    try {
      const {
        first_name,
        last_name,
        current_timezone,
        linkedin_url,
        role,
        secondary_mobile,
        available_secondary_whatsapp,
        available_primary_whatsapp,
        email: newEmail,
        mobile: newMobile,
        calendar_url,
        whatsapp_consent,
      } = values;

      // update name/email/mobile without OTP if admin changed them
      if (isInkleSuperAdmin) {
        const contactBody: {
          profile_id: string;
          first_name?: string;
          last_name?: string;
          email?: string;
          mobile?: string;
        } = { profile_id };
        if (first_name !== initialFirstName || last_name !== initialLastName) {
          contactBody.first_name = first_name;
          contactBody.last_name = last_name;
        }
        if (newEmail && newEmail !== email) contactBody.email = newEmail;
        if (newMobile && newMobile !== mobile) contactBody.mobile = newMobile;
        if (
          contactBody.first_name !== undefined ||
          contactBody.email ||
          contactBody.mobile
        ) {
          await adminUpdateTeamMemberContact({
            groupId,
            body: contactBody,
          }).unwrap();
        }
      }

      let body: {
        email: string;
        group_role_id: string;
        secondary_mobile?: string;
        current_timezone?: string;
        linkedin_url?: string;
        profile_id: string;
        available_secondary_whatsapp?: boolean;
        available_primary_whatsapp?: boolean;
        calendar_url?: string;
        whatsapp_consent?: boolean;
      } = {
        email: newEmail || email,
        group_role_id: role,
        profile_id,
        available_primary_whatsapp,
      };
      if (current_timezone) {
        body = { ...body, current_timezone };
      }
      if (linkedin_url) {
        body = { ...body, linkedin_url };
      }
      if (secondary_mobile) {
        body = { ...body, secondary_mobile, available_secondary_whatsapp };
      }
      if (typeof calendar_url === "string") {
        body.calendar_url = calendar_url;
      }
      if (typeof whatsapp_consent === "boolean") {
        body.whatsapp_consent = whatsapp_consent;
      }
      await updateTeamMember({ id: groupId, body }).unwrap();
      successToast({
        message: "User updated successfully",
        title: "Group user",
      });
      closeModal();
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
      closeModal();
    }
  };

  return (
    <>
      <Modal.Root open={show} onOpenChange={closeModal} modal={false}>
        <Modal.Content asChild useCustomOverlay>
          <FormikForm
            validateOnChange={false}
            enableReinitialize
            initialValues={customerInitialValues}
            validationSchema={manageProfileValidation}
            onSubmit={handleSubmit}
          >
            {({ values, submitForm, setFieldValue }) => {
              const selected = timezoneOptions.find((option) =>
                option.value.includes(values.current_timezone)
              );
              const selectedRole = roleComboboxOptions.find(
                (option) => option.value === values.role
              );

              return (
                <>
                  <Modal.Header>
                    <Modal.Title>Manage profile</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body className="t-flex t-flex-col t-gap-6">
                    <div className="t-flex t-gap-4">
                      <Input
                        required
                        withForm
                        label="First name"
                        name="first_name"
                        placeholder="Enter first name"
                        disabled={!isInkleSuperAdmin}
                      />
                      <Input
                        required
                        withForm
                        label="Last name"
                        name="last_name"
                        placeholder="Enter last name"
                        disabled={!isInkleSuperAdmin}
                      />
                    </div>
                    <Input
                      required
                      withForm
                      label="Email"
                      name="email"
                      placeholder="Enter email"
                      disabled={!isInkleSuperAdmin}
                    />
                    <Combobox
                      required
                      withForm
                      label="Role"
                      name="role"
                      isClearable={false}
                      options={roleComboboxOptions}
                      value={selectedRole || null}
                      isDisabled={!isInkleSuperAdmin || roleUpdateNotAllowed}
                      placeholder="Select role"
                    />
                    <Input
                      withForm
                      label="Calendar booking link"
                      name="calendar_url"
                      placeholder="Enter calendar booking link"
                    />
                    <div>
                      <MobileInput
                        label="Primary number"
                        name="mobile"
                        disabled={!isInkleSuperAdmin}
                      />
                      <div className="t-mt-1.5">
                        <Checkbox
                          label="Available on Whatsapp"
                          name="available_primary_whatsapp"
                          defaultChecked={available_primary_whatsapp}
                          onChange={(e) => {
                            setFieldValue(
                              "available_primary_whatsapp",
                              e.target.checked
                            );
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <MobileInput
                        label="Secondary number"
                        name="secondary_mobile"
                      />
                      <div className="t-mt-1.5">
                        <Checkbox
                          label="Available on Whatsapp"
                          name="available_secondary_whatsapp"
                          defaultChecked={available_secondary_whatsapp}
                          onChange={(e) => {
                            setFieldValue(
                              "available_secondary_whatsapp",
                              e.target.checked
                            );
                          }}
                        />
                      </div>
                    </div>
                    <div className="t-flex t-items-center t-justify-between t-gap-4">
                      <div className="t-shrink-0 t-flex t-justify-center t-items-center">
                        <Switch
                          name="whatsapp_consent"
                          checked={values.whatsapp_consent}
                          defaultChecked={whatsapp_consent}
                          onCheckedChange={() => {
                            if (values.whatsapp_consent === false)
                              confirmWhatsappConsent.open();
                            else setFieldValue("whatsapp_consent", false);
                          }}
                        />
                      </div>
                      <label
                        htmlFor="whatsappConsent"
                        className="t-cursor-pointer t-w-full t-flex t-flex-col t-gap-0.5"
                      >
                        <div className="t-text-subtext-sm t-text-text-60">
                          WhatsApp notifications
                        </div>
                        {typeof values.whatsapp_consent === "boolean" &&
                          !values.whatsapp_consent && (
                            <div className="t-text-body-sm-lite t-text-red">
                              Please ensure you have asked the customer before
                              enabling
                            </div>
                          )}
                      </label>
                    </div>
                    <Combobox
                      withForm
                      label="Time zone"
                      name="current_timezone"
                      options={timezoneOptions}
                      value={selected || null}
                      placeholder="Select timezone"
                      menuPortalTarget={document.body}
                      menuPlacement="top"
                    />
                    <Input
                      withForm
                      label="LinkedIn"
                      name="linkedin_url"
                      placeholder="Your linkedIn url"
                    />
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button type="button">Cancel</Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      type="submit"
                      onClick={submitForm}
                      isLoading={teamMemberUpdating || contactUpdating}
                      disabled={teamMemberUpdating || contactUpdating}
                    >
                      Update
                    </Button>
                  </Modal.FooterButtonGroup>
                  <ConfirmWhatsappConsentModal
                    isOpen={confirmWhatsappConsent.isOpen}
                    close={confirmWhatsappConsent.close}
                    onClose={() => {
                      setFieldValue("whatsapp_consent", true);
                      confirmWhatsappConsent.close();
                    }}
                  />
                </>
              );
            }}
          </FormikForm>
        </Modal.Content>
      </Modal.Root>
      <EmailUpdate
        email={email}
        showEmailModal={emailUpdateModal.isOpen}
        setShowEmailModal={() => emailUpdateModal.close()}
      />
      <MobileUpdate
        showMobileModal={mobileUpdateModal.isOpen}
        setShowMobileModal={() => mobileUpdateModal.close()}
        mobile={mobile}
      />
    </>
  );
};
